import React, { useState } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { requestNotificationPermission, areNotificationsEnabled, getNotificationDebugInfo } from '@/services/notifications/oneSignalService';
import { updatePlayerIdAfterPermission } from '@/services/notifications/playerIdService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth';
import { createLogger } from '@/services/utils/loggingUtils';

const logger = createLogger('NotificationPermissionPrompt');

interface NotificationPermissionPromptProps {
  onDismiss?: () => void;
  showDismissButton?: boolean;
  variant?: 'card' | 'banner';
}

export const NotificationPermissionPrompt: React.FC<NotificationPermissionPromptProps> = ({
  onDismiss,
  showDismissButton = true,
  variant = 'card'
}) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const { toast } = useToast();
  
  // Check notification status on mount and periodically
  React.useEffect(() => {
    const checkNotificationStatus = () => {
      const enabled = areNotificationsEnabled();
      setIsEnabled(enabled);
    };
    
    checkNotificationStatus();
    
    // Check again after a delay in case OneSignal is still initializing
    const timer = setTimeout(checkNotificationStatus, 2000);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Enhanced PWA detection
  const isPWAMode = window.matchMedia('(display-mode: standalone)').matches || 
                   (window.navigator as any).standalone === true ||
                   window.matchMedia('(display-mode: fullscreen)').matches;

  // Simple browser detection
  const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent);

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    
    try {
      // Add a small delay to ensure user interaction context is preserved
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const granted = await requestNotificationPermission();
      
      if (granted) {
        // Update state immediately
        setIsEnabled(true);
        
        toast({
          title: "Notifications enabled!",
          description: "You'll now receive important updates about your reagents and messages.",
        });
        
        // Update player ID in the background with retry
        if (user?.id) {
          updatePlayerIdAfterPermission(user.id).then(() => {
            // Double-check notification status after player ID update
            setTimeout(() => {
              const finalEnabled = areNotificationsEnabled();
              setIsEnabled(finalEnabled);
            }, 3000);
          });
        }
        
        // Delay dismissal to show success state
        setTimeout(() => {
          onDismiss?.();
        }, 1500);
      } else {
        // Provide browser-specific guidance
        let description = "You can enable notifications later in your browser settings.";
        
        if (isMobileSafari) {
          description = "For Safari mobile: Go to Settings > Safari > Notifications, or try refreshing the page and enabling notifications when prompted. You may also need to add this site to your home screen.";
        }
        
        toast({
          title: "Permission denied",
          description,
          variant: "destructive",
        });
      }
    } catch (error) {
      logger.error('Error enabling notifications:', error);
      
      toast({
        title: "Error",
        description: "Failed to enable notifications. Please check your browser settings and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Only show for authenticated users
  if (!user) {
    return null;
  }

  // Don't show if notifications are already enabled
  if (isEnabled) {
    return null;
  }

  const content = (
    <>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <Bell className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">
              Enable Push Notifications
            </h3>
            {showDismissButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDismiss}
                className="h-auto p-1"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Stay updated with instant notifications for new swap offers, messages, and weekly digest summaries.
            {isMobileSafari && !isPWAMode && (
              <span className="block mt-2 p-2 bg-amber-50 text-amber-800 rounded text-xs border border-amber-200">
                <strong>Safari Mobile:</strong> For reliable notifications, add this site to your home screen by tapping the Share button and selecting "Add to Home Screen".
              </span>
            )}
            {isMobileSafari && isPWAMode && (
              <span className="block mt-1 text-xs text-green-600">
                ✓ Running as installed app - notifications should work properly.
              </span>
            )}
          </p>
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleEnableNotifications}
              disabled={isLoading}
              size="sm"
            >
              {isLoading ? "Enabling..." : "Enable Notifications"}
            </Button>
            {showDismissButton && (
              <Button
                variant="outline"
                size="sm"
                onClick={onDismiss}
              >
                Later
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );

  if (variant === 'banner') {
    return (
      <div className="border-b bg-muted/50 p-4">
        <div className="container mx-auto">
          {content}
        </div>
      </div>
    );
  }

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Bell className="h-5 w-5" />
          Stay Connected
        </CardTitle>
        <CardDescription>
          Enable push notifications to get instant updates about your reagent swaps and messages.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex gap-2">
          <Button
            onClick={handleEnableNotifications}
            disabled={isLoading}
            size="sm"
          >
            {isLoading ? "Enabling..." : "Enable Notifications"}
          </Button>
          {showDismissButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDismiss}
            >
              Later
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};